import { TESTIMONIAL_AVATARS } from "@/lib/constants/testimonial-avatars";
import { supabase } from "@/lib/supabase";

export interface Testimonial {
  id: string;
  name: string;
  role: string;
  image_url: string | null;
  content: string;
  rating: number;
  is_featured: boolean;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

// Mock testimonials as fallback
const MOCK_TESTIMONIALS: Testimonial[] = [
  {
    id: "mock-1",
    name: "Sarah L.",
    role: "Locataire",
    image_url: TESTIMONIAL_AVATARS.sarah,
    content:
      "J'ai trouvé la voiture parfaite pour mes vacances à un prix très compétitif. Le processus était simple et l'agence très professionnelle !",
    rating: 5,
    is_featured: true,
    is_active: true,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
  {
    id: "mock-2",
    name: "Mohammed A.",
    role: "Agence partenaire",
    image_url: TESTIMONIAL_AVATARS.mohammed,
    content:
      "RAKB nous a permis de développer notre activité et d'augmenter notre taux d'occupation. La plateforme est vraiment bien pensée et l'équipe très réactive.",
    rating: 5,
    is_featured: true,
    is_active: true,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
  {
    id: "mock-3",
    name: "Karim B.",
    role: "Locataire",
    image_url: TESTIMONIAL_AVATARS.karim,
    content: "Une expérience de location super simple et transparente. Je recommande vivement !",
    rating: 5,
    is_featured: true,
    is_active: true,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
  {
    id: "mock-4",
    name: "Amina T.",
    role: "Locataire",
    image_url: "https://api.dicebear.com/7.x/adventurer/svg?seed=Amina&gender=female",
    content:
      "Service impeccable ! La voiture était en excellent état et la procédure de réservation très fluide. Je reviendrai sans hésiter.",
    rating: 5,
    is_featured: true,
    is_active: true,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
  {
    id: "mock-5",
    name: "Auto Rent Maroc",
    role: "Agence partenaire",
    image_url: "https://api.dicebear.com/7.x/adventurer/svg?seed=AutoRent&gender=male",
    content:
      "Depuis que nous utilisons RAKB, nos revenus ont augmenté de 40%. La gestion de notre flotte est devenue beaucoup plus simple.",
    rating: 5,
    is_featured: true,
    is_active: true,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
];

export const testimonialService = {
  /**
   * Get all active testimonials with fallback to mock data
   */
  async getTestimonials(featuredOnly: boolean = false): Promise<Testimonial[]> {
    try {
      let query = supabase
        .from("testimonials")
        .select(
          "id, name, role, image_url, content, rating, is_featured, is_active, created_at, updated_at"
        )
        .eq("is_active", true)
        .order("created_at", { ascending: false });

      if (featuredOnly) {
        query = query.eq("is_featured", true);
      }

      const { data, error } = await query;

      // If error or no data, return mock testimonials
      if (error || !data || data.length === 0) {
        console.warn("Failed to load testimonials from database, using mock data:", error?.message);
        const mockData = featuredOnly
          ? MOCK_TESTIMONIALS.filter((t) => t.is_featured)
          : MOCK_TESTIMONIALS;
        return mockData;
      }

      return data;
    } catch (error) {
      console.warn("Error loading testimonials, using mock data:", error);
      const mockData = featuredOnly
        ? MOCK_TESTIMONIALS.filter((t) => t.is_featured)
        : MOCK_TESTIMONIALS;
      return mockData;
    }
  },

  /**
   * Get featured testimonials (for homepage) with fallback to mock data
   */
  async getFeaturedTestimonials(limit?: number): Promise<Testimonial[]> {
    try {
      let query = supabase
        .from("testimonials")
        .select(
          "id, name, role, image_url, content, rating, is_featured, is_active, created_at, updated_at"
        )
        .eq("is_active", true)
        .eq("is_featured", true)
        .order("created_at", { ascending: false });

      if (limit) {
        query = query.limit(limit);
      }

      const { data, error } = await query;

      // If error or no data, return mock testimonials
      if (error || !data || data.length === 0) {
        console.warn(
          "Failed to load featured testimonials from database, using mock data:",
          error?.message
        );
        const mockData = limit
          ? MOCK_TESTIMONIALS.filter((t) => t.is_featured).slice(0, limit)
          : MOCK_TESTIMONIALS.filter((t) => t.is_featured);
        return mockData;
      }

      return data;
    } catch (error) {
      console.warn("Error loading featured testimonials, using mock data:", error);
      const mockData = limit
        ? MOCK_TESTIMONIALS.filter((t) => t.is_featured).slice(0, limit)
        : MOCK_TESTIMONIALS.filter((t) => t.is_featured);
      return mockData;
    }
  },
};
